<template>
  <div class="global-container">
    <div class="content">
      <!-- Header Section -->
      <div class="header-section">
        <div class="title-section">
          <h1 class="page-title">User Management</h1>
          <p class="page-description">Manage system users and their access</p>
        </div>
        <button class="add-user-button" @click="openAddUserModal">
          <span class="plus-icon">+</span>
          Add User
        </button>
      </div>

      <!-- Stat Cards -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-content">
            <h3 class="stat-label">Total Users</h3>
            <p class="stat-value">{{ users.length }}</p>
          </div>
          <div class="stat-icon">👥</div>
        </div>
        <div class="stat-card">
          <div class="stat-content">
            <h3 class="stat-label">Owners</h3>
            <p class="stat-value">{{ ownerCount }}</p>
          </div>
          <div class="stat-icon">👑</div>
        </div>
      </div>

      <!-- Search and Filter -->
      <div class="search-section">
        <div class="search-wrapper">
          <span class="search-icon">🔍</span>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search users..."
            class="search-input"
          />
        </div>
      </div>

      <!-- Users Table - Desktop -->
      <div class="table-section" v-if="!isMobileView">
        <table class="users-table">
          <thead>
            <tr class="table-header">
              <th class="col-name">Name</th>
              <th class="col-email">Email</th>
              <th class="col-phone">Phone Number</th>
              <th class="col-role">Role</th>
              <th class="col-created">Created</th>
              <th class="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in filteredUsers" :key="user.id" class="table-row">
              <td class="col-name">
                <div class="user-name-wrapper">
                  <span class="user-name">{{ user.fullName }}</span>
                  <span v-if="user.isCurrentUser" class="you-badge">You</span>
                </div>
              </td>
              <td class="col-email">
                <a :href="`mailto:${user.email}`" class="user-email">{{ user.email }}</a>
              </td>
              <td class="col-phone">{{ user.phoneNumber }}</td>
              <td class="col-role">
                <span class="role-badge" :class="user.role.toLowerCase()">
                  {{ user.role }}
                </span>
              </td>
              <td class="col-created">{{ user.createdAt }}</td>
              <td class="col-actions">
                <button class="action-menu-button" @click="toggleMenu(user.id)">
                  ...
                </button>
                <div v-if="activeMenuId === user.id" class="action-menu">
                  <button class="menu-item" @click="viewUser(user)">View</button>
                  <button class="menu-item" @click="editUser(user)">Edit</button>
                  <button class="menu-item delete" @click="deleteUser(user)">Delete</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Users Cards - Mobile -->
      <div class="mobile-cards" v-else>
        <div
          v-for="user in filteredUsers"
          :key="user.id"
          class="user-card"
        >
          <div class="card-header">
            <div class="card-header-left">
              <span class="user-name">{{ user.fullName }}</span>
              <span v-if="user.isCurrentUser" class="you-badge">You</span>
              <span class="role-badge" :class="user.role.toLowerCase()">
                {{ user.role }}
              </span>
            </div>
            <div class="card-actions">
              <button class="action-menu-button" @click="toggleMenu(user.id)">
                ...
              </button>
              <div v-if="activeMenuId === user.id" class="action-menu">
                <button class="menu-item" @click="viewUser(user)">View</button>
                <button class="menu-item" @click="editUser(user)">Edit</button>
                <button class="menu-item delete" @click="deleteUser(user)">Delete</button>
              </div>
            </div>
          </div>
          <div class="card-body">
            <div class="card-row">
              <span class="card-label">Email</span>
              <a :href="`mailto:${user.email}`" class="card-value user-email">{{ user.email }}</a>
            </div>
            <div class="card-row">
              <span class="card-label">Phone</span>
              <span class="card-value">{{ user.phoneNumber }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">Created</span>
              <span class="card-value">{{ user.createdAt }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add User Modal -->
    <AddUserModal
      :isOpen="isAddUserModalOpen"
      @close="closeAddUserModal"
      @create="handleCreateUser"
      :loading="loading"
    />
  </div>
</template>

<script>
import AddUserModal from '@/components/AddUserModal.vue'
import { mapActions, mapState } from 'vuex'
export default {
  name: 'UserManagement',
  components: {
    AddUserModal
  },
  data() {
    return {
      searchQuery: '',
      activeMenuId: null,
      isAddUserModalOpen: false,
      users: [],
      windowWidth: window.innerWidth
    }
  },
  computed: {
    ...mapState('auth', ['loading']),
    isMobileView() {
      return this.windowWidth < 768
    },
    ownerCount() {
      return this.users.filter(user => user.role === 'Owner').length
    },
    filteredUsers() {
  return this.users.filter(user => {
    const name = (user.fullName || '').toLowerCase()
    const email = (user.email || '').toLowerCase()
    const query = this.searchQuery.toLowerCase()

    return name.includes(query) || email.includes(query)
  })
}

  },
  methods: {
    ...mapActions('user', ['GetUsers']),
    ...mapActions('auth', ['register']),
    openAddUserModal() {
      this.isAddUserModalOpen = true
    },
    closeAddUserModal() {
      this.isAddUserModalOpen = false
    },
    async handleCreateUser(userData) {
      const payload = {
        fullName: userData.fullName,
        email: userData.email,
        phoneNumber: userData.phone,
        role: userData.role,
        password: userData.password
      }
      await this.register(payload)
      this.closeAddUserModal()
      await this.fetchUsers()
    },
    toggleMenu(userId) {
      this.activeMenuId = this.activeMenuId === userId ? null : userId
    },
    viewUser(user) {
      console.log('Viewing user:', user)
      this.activeMenuId = null
    },
    editUser(user) {
      console.log('Editing user:', user)
      this.activeMenuId = null
    },
    deleteUser(user) {
      if (confirm(`Are you sure you want to delete ${user.fullName}?`)) {
        this.users = this.users.filter(u => u.id !== user.id)
        this.activeMenuId = null
      }
    },
    async fetchUsers() {
      const res = await this.GetUsers()
      this.users = res
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    }
  },
  created() {
    this.fetchUsers()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
/* Header Section */
.header-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 40px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.page-description {
  font-size: 15px;
  color: #9ca3af;
  font-weight: 400;
}

/* Add User Button */
.add-user-button {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;
  white-space: nowrap;
}

.add-user-button:hover {
  background: #1d4ed8;
}

.plus-icon {
  font-size: 18px;
}

/* Stat Cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
  max-width: 1200px;
}

.stat-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  margin: 0 0 8px 0;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.stat-icon {
  font-size: 32px;
  margin-left: 16px;
}

/* Search Section */
.search-section {
  margin-bottom: 32px;
}

.search-wrapper {
  position: relative;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
}

.search-input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
}

.search-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

/* Table Section */
.table-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.table-header {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table-header th {
  padding: 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;
}

.table-row:hover {
  background: #f9fafb;
}

.table-row td {
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
}

.col-name {
  font-weight: 600;
  width: 15%;
}

.user-name-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-name {
  color: #1f2937;
  font-weight: 600;
}

.you-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.col-email {
  width: 25%;
}

.user-email {
  color: #2563eb;
  text-decoration: none;
}

.user-email:hover {
  text-decoration: underline;
}

.col-role {
  width: 12%;
}

.role-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.role-badge.owner {
  background: #fca5a5;
  color: #b45309;
}

.role-badge.user {
  background: #cbd5e1;
  color: #475569;
}

.col-created {
  width: 20%;
}

.col-phone {
  width: 20%;
}

.col-actions {
  width: 8%;
  position: relative;
}

.action-menu-button {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6b7280;
  padding: 4px 8px;
  transition: color 0.2s ease;
}

.action-menu-button:hover {
  color: #1f2937;
}

.action-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 140px;
}

.menu-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  text-align: left;
  font-size: 14px;
  color: #1f2937;
  cursor: pointer;
  transition: background 0.2s ease;
}

.menu-item:hover {
  background: #f3f4f6;
}

.menu-item.delete {
  color: #dc2626;
}

.menu-item.delete:hover {
  background: #fee2e2;
}

/* Mobile Cards */
.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.user-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.user-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 8px;
}

.user-card .card-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.user-card .card-actions {
  position: relative;
}

.user-card .card-body {
  padding: 16px;
}

.user-card .card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
}

.user-card .card-row:last-child {
  border-bottom: none;
}

.user-card .card-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.user-card .card-value {
  font-size: 14px;
  color: #1f2937;
  text-align: right;
  word-break: break-all;
  max-width: 60%;
}

/* Responsive */
@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 24px;
  }

  .page-title {
    font-size: 28px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .search-wrapper {
    max-width: 100%;
  }

  .col-name,
  .col-email,
  .col-role,
  .col-created,
  .col-phone,
  .col-actions {
    width: auto;
  }

  .table-header th,
  .table-row td {
    padding: 12px;
    font-size: 12px;
  }
}
</style>
